import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { subscriptionAPI } from "../../api/client";
import "./AuthPages.css";
import "./SubscriptionPage.css";

// Terms and prices are resolved server-side (see billing.service
// getEffectivePricingForTenant). A tenant on a negotiated contract has entirely
// different amounts and a restricted set of cycles, so computing anything here
// would show a figure the order endpoint would then refuse.
const DURATION_LABELS = {
  1: "1 Month",
  3: "3 Months",
  6: "6 Months",
  12: "1 Year",
};

function durationLabel(months) {
  return DURATION_LABELS[months] || `${months} Months`;
}

function formatInr(value) {
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(Math.max(0, Number(value) || 0));
}

function formatDate(value) {
  if (!value) return "-";
  return new Date(value).toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function loadRazorpayScript() {
  return new Promise((resolve, reject) => {
    if (window.Razorpay) {
      resolve(true);
      return;
    }
    const existing = document.querySelector('script[data-razorpay="true"]');
    if (existing) {
      existing.addEventListener("load", () => resolve(true));
      existing.addEventListener("error", () =>
        reject(new Error("Failed to load Razorpay SDK")),
      );
      return;
    }
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.async = true;
    script.dataset.razorpay = "true";
    script.onload = () => resolve(true);
    script.onerror = () => reject(new Error("Failed to load Razorpay SDK"));
    document.body.appendChild(script);
  });
}

export default function SubscriptionPage() {
  const navigate = useNavigate();

  const [status, setStatus] = useState(null);
  const [userCount, setUserCount] = useState(10);
  const [durationMonths, setDurationMonths] = useState(null);
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  // Drives the blocking overlay. Verification makes a live Razorpay API call,
  // so there are several seconds after the modal closes where the page would
  // otherwise sit there looking frozen.
  const [phase, setPhase] = useState("idle");
  const [result, setResult] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    Promise.all([
      subscriptionAPI.getStatus().catch(() => null),
      subscriptionAPI.getPricing().catch(() => null),
    ])
      .then(([statusResp, pricingResp]) => {
        if (statusResp?.data) {
          setStatus(statusResp.data);
          if (statusResp.data.totalUsersRegistered) {
            setUserCount(statusResp.data.totalUsersRegistered);
          }
        }
        if (pricingResp?.data) {
          setQuote(pricingResp.data);
          const first = pricingResp.data.options?.[0];
          if (first) setDurationMonths(first.durationMonths);
          // A contracted seat cap is not the customer's to change.
          if (pricingResp.data.includedUsers) {
            setUserCount(pricingResp.data.includedUsers);
          }
        }
      })
      .finally(() => setPageLoading(false));
  }, []);

  const loadHistory = () => {
    setHistoryLoading(true);
    subscriptionAPI
      .getHistory()
      .then((resp) => setHistory(resp.data || []))
      .catch(() => setHistory([]))
      .finally(() => setHistoryLoading(false));
  };

  useEffect(() => {
    loadHistory();
  }, []);

  const options = useMemo(() => quote?.options || [], [quote]);
  const isNegotiated = Boolean(quote?.isNegotiated);
  const seatsLocked = Boolean(quote?.includedUsers);

  const selected = useMemo(
    () => options.find((o) => o.durationMonths === durationMonths) || options[0] || null,
    [options, durationMonths],
  );

  const selectedDuration = {
    months: selected?.durationMonths ?? 1,
    label: durationLabel(selected?.durationMonths ?? 1),
  };

  const pricing = {
    users: selected?.users ?? Math.max(1, Number(userCount) || 1),
    gross: selected?.grossAmount ?? 0,
    discountAmount: selected?.discountAmount ?? 0,
    total: selected?.totalAmount ?? 0,
    discount: selected?.discountPercent ?? 0,
  };

  const openRazorpayCheckout = ({ order, keyId }) =>
    new Promise((resolve, reject) => {
      const rzp = new window.Razorpay({
        key: keyId,
        amount: order.amount,
        currency: order.currency,
        name: status?.planType ? "Subscription Renewal" : "DecisionFlow Pro",
        description: `Pro Plan - ${selectedDuration.label}`,
        order_id: order.id,
        handler: (response) =>
          resolve({
            provider: "RAZORPAY",
            orderId: response.razorpay_order_id,
            paymentId: response.razorpay_payment_id,
            signature: response.razorpay_signature,
          }),
        notes: {
          planType: "PRO",
          totalUsers: String(pricing.users),
          durationMonths: String(selectedDuration.months),
          type: "EXTENSION",
        },
        theme: { color: "#0f62c7" },
        modal: {
          ondismiss: () => {
            const err = new Error("Payment was cancelled");
            err.cancelled = true;
            reject(err);
          },
        },
      });

      // Without this the modal reports the decline internally and the page
      // only learns about it if the user then dismisses, which reads as a
      // generic "cancelled" rather than the real reason.
      rzp.on("payment.failed", (resp) => {
        const err = new Error(
          resp?.error?.description || "Payment failed. You were not charged.",
        );
        err.failed = true;
        err.code = resp?.error?.code;
        err.reason = resp?.error?.reason;
        reject(err);
      });

      rzp.open();
    });

  const handleDownloadInvoice = async (paymentId, invoiceNumber) => {
    setDownloadingId(paymentId);
    try {
      const resp = await subscriptionAPI.downloadInvoice(paymentId);
      const url = window.URL.createObjectURL(
        new Blob([resp.data], { type: "application/pdf" }),
      );
      const link = document.createElement("a");
      link.href = url;
      link.download = `${invoiceNumber || "invoice"}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError("Could not download that invoice. Please try again.");
    } finally {
      setDownloadingId(null);
    }
  };

  const handlePay = async () => {
    setError("");
    setResult(null);
    setLoading(true);
    setPhase("creating_order");
    // Tracked locally as well: `phase` in this closure is the value from the
    // render that created this handler, so it never observes our own setPhase
    // calls and cannot be used to tell a decline from a post-charge failure.
    let stage = "creating_order";
    try {
      await loadRazorpayScript();
      const orderResp = await subscriptionAPI.createOrder(
        pricing.users,
        selectedDuration.months,
      );

      stage = "awaiting_payment";
      setPhase("awaiting_payment");
      const paymentPayload = await openRazorpayCheckout({
        order: {
          id: orderResp.data.orderId,
          amount: orderResp.data.amount,
          currency: orderResp.data.currency,
        },
        keyId: orderResp.data.keyId,
      });

      // Money has left the customer at this point. Everything below is our
      // verification, so the overlay must stay up and must not look cancellable.
      stage = "verifying";
      setPhase("verifying");
      const extendResp = await subscriptionAPI.extend({
        payment: paymentPayload,
        durationMonths: selectedDuration.months,
        totalUsers: pricing.users,
      });

      setResult({
        ok: true,
        amount: pricing.total,
        paymentId: extendResp.data?.paymentId || null,
        invoiceNumber: extendResp.data?.invoiceNumber || null,
        nextPaymentDue: extendResp.data?.nextPaymentDue || null,
      });
      setPhase("success");
      loadHistory();
      subscriptionAPI
        .getStatus()
        .then((resp) => setStatus(resp.data))
        .catch(() => {});
    } catch (err) {
      const message =
        err.response?.data?.error || err.response?.data?.message || err.message;

      setResult({
        ok: false,
        cancelled: Boolean(err.cancelled),
        // A failure after the modal closed means we may have taken money we
        // could not verify, which needs a different message than a decline.
        afterCharge: stage === "verifying",
        message,
      });
      setPhase("failed");
    } finally {
      setLoading(false);
    }
  };

  const resetFlow = () => {
    setPhase("idle");
    setResult(null);
    setError("");
  };

  if (pageLoading)
    return (
      <div className="sub-page-shell">
        <div className="sub-loading">Loading...</div>
      </div>
    );

  const busy = phase === "creating_order" || phase === "verifying";

  if (phase === "success" && result?.ok) {
    return (
      <div className="sub-page-shell">
        <div className="sub-container">
          <div className="sub-result sub-result-ok">
            <div className="sub-result-icon">&#10003;</div>
            <h2>Payment successful</h2>
            <p>
              {formatInr(result.amount)} received. Your plan is active until{" "}
              <strong>{formatDate(result.nextPaymentDue)}</strong>.
            </p>
            {result.invoiceNumber && (
              <p className="sub-result-meta">
                Invoice <span className="sub-history-mono">{result.invoiceNumber}</span>{" "}
                has also been emailed to you.
              </p>
            )}
            <div className="sub-result-actions">
              {result.paymentId && (
                <button
                  type="button"
                  className="auth-btn auth-btn-primary"
                  disabled={downloadingId === result.paymentId}
                  onClick={() =>
                    handleDownloadInvoice(result.paymentId, result.invoiceNumber)
                  }
                >
                  {downloadingId === result.paymentId
                    ? "Preparing..."
                    : "Download Invoice"}
                </button>
              )}
              <button
                type="button"
                className="auth-btn auth-btn-secondary"
                onClick={() => navigate("/dashboard")}
              >
                Go to Dashboard
              </button>
            </div>
            {error && <div className="auth-alert" style={{ marginTop: 12 }}>{error}</div>}
          </div>
        </div>
      </div>
    );
  }

  if (phase === "failed" && result && !result.ok) {
    return (
      <div className="sub-page-shell">
        <div className="sub-container">
          <div className={`sub-result ${result.cancelled ? "" : "sub-result-bad"}`}>
            <div className="sub-result-icon">{result.cancelled ? "!" : "\u00d7"}</div>
            <h2>{result.cancelled ? "Payment cancelled" : "Payment did not complete"}</h2>
            <p>{result.message}</p>
            {result.afterCharge && !result.cancelled && (
              <p className="sub-result-meta">
                If your account was debited, do not pay again — contact support
                with the time of this attempt and it will be reconciled or
                refunded.
              </p>
            )}
            <div className="sub-result-actions">
              <button
                type="button"
                className="auth-btn auth-btn-primary"
                onClick={resetFlow}
              >
                Try Again
              </button>
              <button
                type="button"
                className="auth-btn auth-btn-secondary"
                onClick={() => navigate(-1)}
              >
                Back
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="sub-page-shell">
      {busy && (
        <div className="sub-overlay" role="status" aria-live="polite">
          <div className="sub-overlay-card">
            <div className="sub-spinner" />
            <strong>
              {phase === "creating_order"
                ? "Preparing your payment..."
                : "Confirming your payment..."}
            </strong>
            <span>
              {phase === "creating_order"
                ? "Opening the secure checkout window."
                : "Please do not close or refresh this page."}
            </span>
          </div>
        </div>
      )}
      <div className="sub-container">
        <div className="sub-header">
          <h1>Manage Subscription</h1>
          {status && (
            <div
              className={`sub-status-badge ${status.isExpired ? "expired" : status.warningLevel || "active"}`}
            >
              {status.isExpired
                ? "Plan Expired"
                : status.daysRemaining === null
                  ? "Active"
                  : status.daysRemaining <= 1
                    ? "Expires today!"
                    : status.planType === "TRIAL"
                      ? `Trial ${status.daysRemaining} day(s) remaining`
                      : status.warningLevel
                        ? `${status.daysRemaining} days remaining`
                        : `Active ${status.daysRemaining} days remaining`}
            </div>
          )}
        </div>

        {status && (
          <div className="sub-current-plan">
            <div className="sub-plan-row">
              <span className="sub-plan-label">Current Plan</span>
              <strong>{status.planType}</strong>
            </div>
            <div className="sub-plan-row">
              <span className="sub-plan-label">Status</span>
              <strong>{status.paymentStatus}</strong>
            </div>
            <div className="sub-plan-row">
              <span className="sub-plan-label">Expires On</span>
              <strong>{formatDate(status.nextPaymentDue)}</strong>
            </div>
            <div className="sub-plan-row">
              <span className="sub-plan-label">Users (active / licensed)</span>
              <strong>
                {status.totalUsersPresent} / {status.totalUsersRegistered}
              </strong>
            </div>
          </div>
        )}

        <div className="sub-section-title">Extend with Pro Plan</div>

        <div className="register-user-count-row">
          <label htmlFor="sub-users">Total Users</label>
          <input
            id="sub-users"
            type="number"
            min={1}
            className="auth-input"
            value={userCount}
            disabled={seatsLocked}
            onChange={(e) => setUserCount(e.target.value)}
          />
        </div>
        {seatsLocked && (
          <p className="register-summary-note">
            Your plan covers up to {quote.includedUsers} users. Contact us to
            change your seat count.
          </p>
        )}

        <div className="register-duration-list" style={{ marginTop: 12 }}>
          {options.map((option) => (
            <button
              key={option.durationMonths}
              type="button"
              className={`register-duration-chip ${
                selected?.durationMonths === option.durationMonths ? "selected" : ""
              }`}
              onClick={() => setDurationMonths(option.durationMonths)}
            >
              {durationLabel(option.durationMonths)}
              {option.discountPercent > 0 && (
                <span style={{ marginLeft: 4, fontSize: 10, color: "#0f62fe" }}>
                  -{option.discountPercent}%
                </span>
              )}
            </button>
          ))}
        </div>

        <div className="register-pricing-summary" style={{ marginTop: 14 }}>
          <h4>Payment Summary</h4>
          <p>
            Pro plan for <strong>{pricing.users}</strong> users over{" "}
            <strong>{selectedDuration.label}</strong>.
          </p>
          {isNegotiated ? (
            <p className="register-summary-note">
              Contracted rate for your account
              {quote?.negotiationNote ? ` - ${quote.negotiationNote}` : ""}.
            </p>
          ) : (
            <>
              <p>Base Amount: {formatInr(pricing.gross)}</p>
              {pricing.discount > 0 && (
                <p>
                  Discount ({pricing.discount}%): -
                  {formatInr(pricing.discountAmount)}
                </p>
              )}
            </>
          )}
          <p className="register-summary-total">
            Total Payable: {formatInr(pricing.total)}
          </p>
          <p className="register-summary-note">
            Plan will be extended from{" "}
            {status?.isExpired ? "today" : formatDate(status?.nextPaymentDue)}{" "}
            by {selectedDuration.label}.
          </p>

          {error && (
            <div className="auth-alert" style={{ marginTop: 8 }}>
              {error}
            </div>
          )}
          <button
            type="button"
            className="auth-btn auth-btn-primary"
            style={{ marginTop: 12, width: "100%" }}
            disabled={loading || !selected}
            onClick={handlePay}
          >
            {loading
              ? "Processing..."
              : `Pay ${formatInr(pricing.total)} & Extend`}
          </button>
        </div>

        <div className="sub-section-title">Payment History</div>

        {historyLoading ? (
          <div className="sub-history-empty">Loading payment history...</div>
        ) : history.length === 0 ? (
          <div className="sub-history-empty">
            No payments yet. Invoices are emailed automatically after each
            payment.
          </div>
        ) : (
          <div className="sub-history-wrap">
            <table className="sub-history-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Invoice</th>
                  <th>Plan</th>
                  <th>Term</th>
                  <th>Users</th>
                  <th className="sub-history-num">Amount</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {history.map((row) => (
                  <tr key={row.id}>
                    <td>{formatDate(row.paidAt)}</td>
                    <td className="sub-history-mono">{row.invoiceNumber || "-"}</td>
                    <td>{row.planType}</td>
                    <td>{row.durationMonths} mo</td>
                    <td>{row.totalUsers}</td>
                    <td className="sub-history-num">
                      {formatInr((row.amountPaise || 0) / 100)}
                    </td>
                    <td className="sub-history-num">
                      <button
                        type="button"
                        className="sub-invoice-btn"
                        disabled={downloadingId === row.id}
                        onClick={() =>
                          handleDownloadInvoice(row.id, row.invoiceNumber)
                        }
                      >
                        {downloadingId === row.id ? "..." : "Invoice"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <button
          type="button"
          className="auth-btn auth-btn-secondary"
          style={{ marginTop: 10, width: "100%" }}
          onClick={() => navigate(-1)}
          disabled={loading}
        >
          &larr; Back
        </button>
      </div>
    </div>
  );
}
